import React, { useState, useEffect } from 'react';
import authFetch from '../utils/authFetch';
import { WarningCircle, XCircle, UsersThree } from '@phosphor-icons/react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';

function JoinCoach({ inviteCode, userId, userEmail, userName, onSuccess, onCancel }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [error, setError] = useState(null);
  const [alreadyHasCoach, setAlreadyHasCoach] = useState(null);

  useEffect(() => {
    checkInviteAndStatus();
  }, [inviteCode, userId]);

  const checkInviteAndStatus = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // בדיקה אם כבר יש למתאמן מאמן
      const myCoachRes = await authFetch(`${API_BASE_URL}/athlete/my-coach?user_id=${userId}`);
      if (myCoachRes.ok) {
        const myCoachData = await myCoachRes.json();
        if (myCoachData.has_coach) {
          setAlreadyHasCoach(myCoachData);
          setIsLoading(false);
          return;
        }
      }
      
      // בדיקת תוקף ההזמנה
      const inviteRes = await authFetch(`${API_BASE_URL}/coach/invite-info/${inviteCode}`);
      if (inviteRes.ok) {
        const inviteData = await inviteRes.json();
        setInviteInfo(inviteData);
      } else {
        const errData = await inviteRes.json();
        setError(errData.detail || 'קוד הזמנה לא תקין');
      }
      
    } catch (err) {
      console.error('Error checking invite:', err);
      setError('שגיאה בטעינת פרטי ההזמנה');
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoin = async () => {
    setIsJoining(true);
    setError(null);
    
    try {
      const res = await fetch(
        `${API_BASE_URL}/coach/join/${inviteCode}?athlete_user_id=${userId}&athlete_email=${encodeURIComponent(userEmail)}&athlete_name=${encodeURIComponent(userName || '')}`,
        { method: 'POST' }
      );
      
      const data = await res.json();
      
      if (data.success) {
        // הצלחה!
        if (onSuccess) {
          onSuccess(data);
        }
      } else {
        setError(data.detail || 'שגיאה בהצטרפות');
      }
      
    } catch (err) {
      console.error('Error joining:', err);
      setError('שגיאה בהצטרפות לקבוצה');
    } finally {
      setIsJoining(false);
    }
  };

  // מסך טעינה
  if (isLoading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-pressed flex items-center justify-center mb-4 animate-pulse">
            <img src="/icons/ritmoiq-r.png" alt="RitmoIQ" className="w-full h-full rounded-2xl object-cover" />
          </div>
          <p className="text-text-muted">בודק הזמנה...</p>
        </div>
      </div>
    );
  }

  // כבר יש מאמן
  if (alreadyHasCoach) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-4" dir="rtl">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-pressed text-text-body flex items-center justify-center mb-6">
            <WarningCircle size={36} weight="bold" aria-hidden="true" />
          </div>
          
          <h1 className="text-2xl font-bold text-text mb-4">כבר יש לך מאמן</h1>
          
          <p className="text-text-muted mb-6">
            אתה כבר משויך למאמן <span className="text-text-body font-medium">{alreadyHasCoach.coach_name}</span>
          </p>
          
          <div className="p-4 rounded-xl bg-surface border border-border mb-6">
            <p className="text-text-muted text-sm">
              אם ברצונך להחליף מאמן, פנה למאמן הנוכחי שלך לביטול השיוך
            </p>
          </div>
          
          <button
            onClick={onCancel}
            className="w-full h-12 rounded-full border border-border text-text-body font-semibold"
          >
            חזור לדף הבית
          </button>
        </div>
      </div>
    );
  }

  // שגיאה - הזמנה לא תקינה
  if (error && !inviteInfo) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-4" dir="rtl">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-pressed text-text-body flex items-center justify-center mb-6">
            <XCircle size={36} weight="bold" aria-hidden="true" />
          </div>
          
          <h1 className="text-2xl font-bold text-text mb-4">הזמנה לא תקינה</h1>
          
          <p className="text-text-muted mb-6">{error}</p>
          
          <div className="p-4 rounded-xl bg-surface border border-border mb-6">
            <p className="text-text-muted text-sm">
              ייתכן שההזמנה פגה או שכבר נוצלה. בקש מהמאמן לשלוח הזמנה חדשה.
            </p>
          </div>
          
          <button
            onClick={onCancel}
            className="w-full h-12 rounded-full border border-border text-text-body font-semibold"
          >
            חזור לדף הבית
          </button>
        </div>
      </div>
    );
  }

  // מסך הצטרפות
  return (
    <div className="min-h-screen bg-bg" dir="rtl">
      
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          
          {/* Coach Avatar */}
          <div className="text-center mb-8">
            <div className="w-24 h-24 mx-auto rounded-full bg-pressed text-accent flex items-center justify-center mb-4">
              <UsersThree size={44} weight="bold" aria-hidden="true" />
            </div>
            
            <h1 className="text-2xl font-bold text-text mb-2">
              הצטרף לקבוצה של {inviteInfo?.coach_name || 'המאמן'}!
            </h1>
            
            <p className="text-text-muted">
              {inviteInfo?.coach_name || 'המאמן'} הזמין אותך להצטרף לקבוצת הריצה
            </p>
          </div>
          
          {/* What you get */}
          <div className="bg-surface rounded-2xl p-6 border border-border mb-6">
            <h3 className="text-text font-semibold mb-4">מה תקבל?</h3>
            <ul className="space-y-3 text-text-muted">
              <li className="flex items-center gap-3">
                <span className="text-accent">✓</span>
                <span>תכנית אימונים שבועית מותאמת אישית</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="text-accent">✓</span>
                <span>מעקב אחר הריצות שלך</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="text-accent">✓</span>
                <span>ליווי והכוונה מהמאמן</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="text-accent">✓</span>
                <span>הכנה למרוצים ותחרויות</span>
              </li>
            </ul>
          </div>
          
          {/* User Info */}
          <div className="bg-surface rounded-xl p-4 border border-border mb-6">
            <p className="text-text-muted text-sm mb-1">מצטרף בתור:</p>
            <p className="text-text font-medium">{userName || userEmail}</p>
            <p className="text-text-muted text-sm">{userEmail}</p>
          </div>
          
          {/* Error message */}
          {error && (
            <div className="p-4 rounded-xl bg-pressed border border-border mb-4">
              <p className="text-text-body text-sm">{error}</p>
            </div>
          )}
          
          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={handleJoin}
              disabled={isJoining}
              className="w-full h-14 rounded-full btn-primary text-lg transition"
            >
              {isJoining ? 'מצטרף...' : 'הצטרף לקבוצה'}
            </button>
            
            <button
              onClick={onCancel}
              disabled={isJoining}
              className="w-full h-12 rounded-full border border-border text-text-body font-semibold"
            >
              לא עכשיו
            </button>
          </div>
          
          <p className="text-center text-text-muted text-xs mt-6">
            בלחיצה על "הצטרף" אתה מאשר שהמאמן יוכל לצפות בנתוני האימונים שלך
          </p>
          
        </div>
      </div>
    </div>
  );
}

export default JoinCoach;
